import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Mail, Phone, Send } from 'lucide-react';
import { toast } from 'sonner';
import { siteInfo } from '../data/content';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    consultationType: 'virtual'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          consultationType: 'virtual'
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(
        error.response?.data?.detail || 
        'Unable to submit your request. Please try again or contact us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-6" style={{ color: '#5E7F8A' }}>
            Schedule Your Free Consultation
          </h1>
          <p className="text-xl max-w-3xl mx-auto mb-8" style={{ color: '#5E7F8A' }}>
            Take the first step toward peaceful resolution. Contact us to schedule a free 20-30 minute consultation.
          </p>
          <div className="w-24 h-1 mx-auto" style={{ backgroundColor: '#9BB2A2' }}></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6" style={{ color: '#5E7F8A' }}>
                  Book Your Consultation
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName" style={{ color: '#5E7F8A' }}>
                        First Name *
                      </Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName" style={{ color: '#5E7F8A' }}>
                        Last Name *
                      </Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" style={{ color: '#5E7F8A' }}>
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" style={{ color: '#5E7F8A' }}>
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="consultationType" style={{ color: '#5E7F8A' }}>
                      Consultation Preference *
                    </Label>
                    <select
                      id="consultationType"
                      name="consultationType"
                      value={formData.consultationType}
                      onChange={handleChange}
                      required
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                      style={{ 
                        color: '#5E7F8A',
                        focusRing: '#9BB2A2'
                      }}
                    >
                      <option value="virtual">Virtual (Video Call)</option>
                      <option value="in-person">In-Person</option>
                      <option value="phone">Phone Call</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="subject" style={{ color: '#5E7F8A' }}>
                      Subject
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="mt-1"
                      placeholder="e.g., Divorce Mediation, Parenting Plan"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" style={{ color: '#5E7F8A' }}>
                      Tell Us About Your Situation
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="mt-1"
                      placeholder="Brief description of your situation and what you hope to achieve..."
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full text-white py-6 text-lg font-semibold transition-all hover:shadow-lg"
                    style={{ backgroundColor: '#9BB2A2' }}
                    onMouseEnter={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = '#8FA596')}
                    onMouseLeave={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = '#9BB2A2')}
                  >
                    {isSubmitting ? 'Submitting...' : (
                      <>
                        <Send size={20} className="mr-2" />
                        Submit Request
                      </>
                    )}
                  </Button>

                  <p className="text-sm text-center" style={{ color: '#5E7F8A' }}>
                    We will respond within 24 hours to schedule your consultation.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Information */}
          <div>
            <div className="mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_e8a7a8e1-af64-416b-ba2f-55c511897d70/artifacts/s46p61en_Contact%20photo.png" 
                alt="Professional mediation consultation"
                className="w-full h-64 object-cover rounded-lg shadow-lg"
              />
            </div>

            <Card className="border-0 shadow-lg mb-8">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6" style={{ color: '#5E7F8A' }}>
                  Contact Information
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#9BB2A2' }}
                    >
                      <Phone size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1" style={{ color: '#5E7F8A' }}>
                        Phone
                      </p>
                      <a 
                        href={`tel:${siteInfo.phone}`}
                        className="transition-opacity hover:opacity-80"
                        style={{ color: '#5E7F8A' }}
                      >
                        {siteInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#9BB2A2' }}
                    >
                      <Mail size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1" style={{ color: '#5E7F8A' }}>
                        Email
                      </p>
                      <a 
                        href={`mailto:${siteInfo.email}`}
                        className="transition-opacity hover:opacity-80"
                        style={{ color: '#5E7F8A' }}
                      >
                        {siteInfo.email}
                      </a>
                    </div>
                  </div>

                </div>
              </CardContent>
            </Card>

            <div className="rounded-lg p-8 text-white" style={{ backgroundColor: '#5E7F8A' }}>
              <h3 className="text-xl font-bold mb-4">
                What to Expect
              </h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Free 20-30 minute initial consultation</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>No obligation to move forward</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Learn about our process and costs</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Discuss your specific situation privately</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
